import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import PropTypes from 'prop-types'
import { useContext, useMemo } from 'react'
import { useForm } from 'react-hook-form'

import { Button } from '@/components/elements/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/elements/select'
import { Switch } from '@/components/elements/switch'
import { Textarea } from '@/components/elements/textarea'
import { FileUploader } from '@/components/FileUploader'
import { SlidePanel } from '@/components/SlidePanel'
import { addEntity, updateEntity } from '@/services/entities.service'
import {
  addOrganizationAccount,
  getOrganizations,
  updateOrganizationAccount,
} from '@/services/organization.service'
import { UserStoreContext } from '@/stores/UserStore'
import { toast } from '@/utils/helpers'

// Sentinel for "no organization" — Radix Select can't use an empty-string value.
const NO_ORG = 'none'

const DEFAULT_VALUES = {
  name: '',
  type: 'Business',
  logoPath: null,
  facilityCode: '',
  streetAddress: '',
  streetAddress2: '',
  city: '',
  state: '',
  zipCode: '',
  email: '',
  phoneNumber: '',
  welcomeMessage: '',
  welcomeVideoPath: null,
  // Parent organization (RSCX-320). NO_ORG = standalone. Only RescueX Admins
  // (this slideout) can set it — the API drops it from non-admin writes.
  organization: NO_ORG,
  // Account feature switches (access_*). Default off; only RescueX Admins (this
  // slideout) can change them — the API drops these fields from non-admin writes.
  accessCustomCourses: false,
}

const AddEditAccountSlideout = ({ editAccount, resetAndClose, organizationId = null }) => {
  // Org-scoped mode (RSCX-320): when an org manages its own accounts, writes go
  // through the org endpoint (auto-links to the org), and the org + feature
  // access sections are hidden — those are RescueX-Admin-only.
  const isOrgScoped = Boolean(organizationId)
  // Org-facing copy calls accounts "Facilities" (RSCX-320).
  const noun = isOrgScoped ? 'Facility' : 'Account'
  const { updateEntityInRoles } = useContext(UserStoreContext)
  const defaultValues = useMemo(() => {
    if (!editAccount) return DEFAULT_VALUES

    const {
      id,
      name,
      type,
      logoPath,
      facilityCode,
      streetAddress,
      streetAddress2,
      city,
      state,
      zipCode,
      email,
      phoneNumber,
      welcomeMessage,
      welcomeVideoPath,
      organization,
      accessCustomCourses,
    } = editAccount

    return {
      ...DEFAULT_VALUES,
      id,
      name,
      type,
      logoPath: logoPath || null,
      facilityCode: facilityCode || '',
      streetAddress: streetAddress || '',
      streetAddress2: streetAddress2 || '',
      city: city || '',
      state: state || '',
      zipCode: zipCode || '',
      email: email || '',
      phoneNumber: phoneNumber || '',
      welcomeMessage: welcomeMessage || '',
      welcomeVideoPath: welcomeVideoPath || null,
      organization: organization || NO_ORG,
      accessCustomCourses: accessCustomCourses ?? false,
    }
  }, [editAccount])

  const form = useForm({ defaultValues })

  // Organizations available to link this account to (RSCX-320).
  const { data: organizations } = useQuery({
    queryKey: ['organizations', 'all'],
    queryFn: () => getOrganizations({ pagination: { pageSize: 1000, pageIndex: 0 } }),
    placeholderData: keepPreviousData,
  })

  // Watch file upload fields
  const watchLogo = form.watch('logoPath')
  const watchWelcomeVideo = form.watch('welcomeVideoPath')

  const resetForm = () => {
    form.reset(DEFAULT_VALUES)
    resetAndClose()
  }

  // Shared render function for file uploads
  const renderFileUpload = ({
    fieldName,
    label,
    acceptedFileTypes,
    filePrefix,
    signedPathKey,
    fileKey,
    watchValue,
    renderPreview,
  }) => {
    const signedPath = editAccount?.[signedPathKey]
    const originalFile = editAccount?.[fileKey]

    // Show preview if signed path exists and file is set
    if (signedPath && watchValue !== null) {
      return (
        <div className="flex flex-col gap-2">
          {renderPreview(signedPath)}
          <Button
            type="button"
            variant="destructive"
            onClick={() => form.setValue(fieldName, null)}
            className="w-fit"
            disabled={isPending}
          >
            Remove {label}
          </Button>
        </div>
      )
    }

    // Show FileUploader if no file or file was removed
    if (!originalFile || watchValue !== originalFile) {
      return (
        <FormField
          control={form.control}
          name={fieldName}
          render={() => (
            <FormItem>
              <FormLabel>{label}</FormLabel>
              <FormControl>
                <FileUploader
                  acceptedFileTypes={acceptedFileTypes}
                  handleUploadToServer={async (file) => {
                    form.setValue(fieldName, file.fileUploadName)
                  }}
                  filePrefix={filePrefix}
                  maxFiles={1}
                  type="aws"
                  disabled={isPending}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )
    }

    // Show placeholder with filename
    return (
      <div className="flex flex-col gap-2">
        <div>
          <p className="text-foreground text-sm font-medium">{watchValue}</p>
          <small>Save the account to see the preview.</small>
        </div>
        <Button
          type="button"
          variant="destructive"
          onClick={() => form.setValue(fieldName, null)}
          className="w-fit"
          disabled={isPending}
        >
          Remove {label}
        </Button>
      </div>
    )
  }

  // Data Processing
  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()
  const { isPending, mutate: mutateEntity } = useMutation({
    mutationFn: (data) => {
      if (isOrgScoped) {
        return editAccount
          ? updateOrganizationAccount({ organizationId, id: editAccount.id, ...data })
          : addOrganizationAccount({ organizationId, ...data })
      }
      return editAccount ? updateEntity(data) : addEntity(data)
    },
    onError: (err) => handleError(err.message),
    onSuccess: (data) => {
      handleSuccess(`${data.name} ${editAccount ? 'updated' : 'added'} successfully`)
      resetForm()

      // Update entity in MobX store so sidebar logo refreshes immediately
      if (editAccount) updateEntityInRoles(data)
      // Refresh Entities lists for the EntitySelector and Accounts page
      queryClient.invalidateQueries({ queryKey: ['all-entities'] })
      queryClient.invalidateQueries({ queryKey: ['entities'] })
      // Refresh the single-entity cache so feature-switch (access_*) changes
      // gate the nav/routes immediately, e.g. when an admin switches into the account.
      if (data?.id) queryClient.invalidateQueries({ queryKey: ['entity', data.id] })
      // Org-scoped: refresh the org's account list + dashboard rollup.
      if (isOrgScoped) {
        queryClient.invalidateQueries({ queryKey: ['organization-accounts', organizationId] })
        queryClient.invalidateQueries({ queryKey: ['organization', organizationId, 'summary'] })
      }
    },
  })

  /**
   * Handles form submission. Converts the NO_ORG sentinel back to null so the
   * API unlinks the account from any organization.
   * @param {object} data
   */
  const onSubmit = async (data) => {
    // Org-scoped mode sets the organization server-side; don't send the field.
    if (isOrgScoped) {
      const { organization, ...rest } = data
      return mutateEntity(rest)
    }
    return mutateEntity({
      ...data,
      organization: data.organization === NO_ORG ? null : data.organization,
    })
  }

  return (
    <SlidePanel
      actions={[
        {
          label: 'Cancel',
          onClick: () => resetAndClose(),
          type: 'cancel',
        },
        {
          label: `${editAccount ? 'Update' : 'Add'} ${noun}`,
          onClick: form.handleSubmit(onSubmit),
          type: 'submit',
        },
      ]}
      content={
        <Form {...form}>
          <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(onSubmit)}>
            {/* Account Details Section */}
            <div className="flex flex-col gap-4">
              <span className="leading-6 font-bold">Account Details</span>

              {/* Logo Upload */}
              {renderFileUpload({
                fieldName: 'logoPath',
                label: 'Logo',
                acceptedFileTypes: ['image/*'],
                filePrefix: 'entity-logos',
                signedPathKey: 'logo',
                fileKey: 'logoPath',
                watchValue: watchLogo,
                renderPreview: (signedPath) => (
                  <img
                    src={signedPath}
                    alt="Account Logo"
                    className="h-20 w-20 rounded object-contain"
                  />
                ),
              })}

              {/* Name */}
              <FormField
                control={form.control}
                disabled={isPending}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                rules={{ required: 'This field is required' }}
              />

              {/* Facility Code */}
              <FormField
                control={form.control}
                disabled={isPending}
                name="facilityCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Facility Code</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                rules={{ required: 'This field is required' }}
              />
            </div>

            {/* Address Section */}
            <div className="flex flex-col gap-4">
              <span className="leading-6 font-bold">Address</span>

              {/* Street Address 1 */}
              <FormField
                control={form.control}
                disabled={isPending}
                name="streetAddress"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Street Address 1</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                rules={{ required: 'This field is required' }}
              />

              {/* Street Address 2 */}
              <FormField
                control={form.control}
                disabled={isPending}
                name="streetAddress2"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Street Address 2</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* City */}
              <FormField
                control={form.control}
                disabled={isPending}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                rules={{ required: 'This field is required' }}
              />

              {/* State and Zip Side by Side */}
              <div className="flex flex-row gap-4">
                <FormField
                  control={form.control}
                  disabled={isPending}
                  name="state"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>State</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                  rules={{ required: 'This field is required' }}
                />

                <FormField
                  control={form.control}
                  disabled={isPending}
                  name="zipCode"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>Zip</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                  rules={{ required: 'This field is required' }}
                />
              </div>
            </div>

            {/* Contact Details Section */}
            <div className="flex flex-col gap-4">
              <span className="leading-6 font-bold">Contact Details</span>

              {/* Email */}
              <FormField
                control={form.control}
                disabled={isPending}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                rules={{ required: 'This field is required' }}
              />

              {/* Phone */}
              <FormField
                control={form.control}
                disabled={isPending}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Facility Phone</FormLabel>
                    <FormControl>
                      <Input type="tel" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
                rules={{ required: 'This field is required' }}
              />

              {/* Welcome Message */}
              <FormField
                control={form.control}
                disabled={isPending}
                name="welcomeMessage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Welcome Message</FormLabel>
                    <FormControl>
                      <Textarea {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Welcome Video */}
              {renderFileUpload({
                fieldName: 'welcomeVideoPath',
                label: 'Welcome Video',
                acceptedFileTypes: ['video/mp4', 'video/webm'],
                filePrefix: 'entity-welcome-videos',
                signedPathKey: 'welcomeVideo',
                fileKey: 'welcomeVideoPath',
                watchValue: watchWelcomeVideo,
                renderPreview: (signedPath) => (
                  <a
                    href={signedPath}
                    download
                    rel="noreferrer"
                    className="text-foreground hover:text-primary flex items-center gap-2 text-sm font-medium"
                  >
                    <Download className="h-4 w-4" />
                    <span>Download Welcome Video</span>
                  </a>
                ),
              })}
            </div>

            {/* Organization Section — links this account to a parent org (RSCX-320).
                Hidden in org-scoped mode: the account is auto-linked to the org. */}
            {!isOrgScoped && (
              <div className="flex flex-col gap-4">
                <span className="leading-6 font-bold">Organization</span>

                <FormField
                  control={form.control}
                  disabled={isPending}
                  name="organization"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Parent Organization</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={isPending}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="None (standalone account)" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={NO_ORG}>None (standalone account)</SelectItem>
                          {(organizations?.results ?? []).map((org) => (
                            <SelectItem key={org.id} value={org.id}>
                              {org.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-muted-foreground text-xs">
                        Owners/Admins of the parent organization get full admin access to this
                        account.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Feature Access Section — RescueX-Admin-only subscription switches.
                Hidden in org-scoped mode (these are RescueX-Admin-only). */}
            {!isOrgScoped && (
              <div className="flex flex-col gap-4">
                <span className="leading-6 font-bold">Feature Access</span>

                <FormField
                  control={form.control}
                  disabled={isPending}
                  name="accessCustomCourses"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between gap-4">
                      <div className="flex flex-col gap-0.5">
                        <FormLabel>Custom Courses</FormLabel>
                        <p className="text-muted-foreground text-xs">
                          Lets this account build and manage its own course modules, custom
                          courses, and certificates.
                        </p>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={field.disabled}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            )}
          </form>
        </Form>
      }
      loading={isPending}
      title={`${editAccount ? 'Update' : 'Add'} ${noun}`}
      setOpen={resetForm}
    />
  )
}

AddEditAccountSlideout.propTypes = {
  editAccount: PropTypes.object,
  resetAndClose: PropTypes.func.isRequired,
  organizationId: PropTypes.string,
}

export default AddEditAccountSlideout
